import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../i18n/LanguageSelector';
import { useTheme } from '../contexts/ThemeContext';
import { Mail, CheckCircle, Clock, RefreshCw } from 'lucide-react';

export default function StandaloneRegisterPage() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    name: '',
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Email verification state
  const [verificationToken, setVerificationToken] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [countdownSeconds, setCountdownSeconds] = useState(0);

  // Countdown timer effect
  useEffect(() => {
    if (countdownSeconds > 0) {
      const timer = setTimeout(() => {
        setCountdownSeconds(countdownSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (otpExpiresAt && new Date(otpExpiresAt) > new Date()) {
      const diff = Math.floor((new Date(otpExpiresAt) - new Date()) / 1000);
      if (diff > 0) {
        setCountdownSeconds(diff);
      }
    }
  }, [countdownSeconds, otpExpiresAt]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Reset verification if email changes
    if (name === 'email' && emailVerified) {
      setEmailVerified(false);
      setVerificationToken(null);
      setOtpCode('');
      setCountdownSeconds(0);
    }
  };

  const handleVerifyEmail = async () => {
    const email = formData.email.trim();
    
    // Validate email locally
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage('Inserisci un\'email valida');
      return;
    }
    
    setIsVerifyingEmail(true);
    setMessage('');
    
    try {
      const response = await fetch('/api/email_verification.php?action=request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (data.success && data.verification_token) {
        setVerificationToken(data.verification_token);
        if (data.expires_at) {
          setOtpExpiresAt(data.expires_at);
          const diff = Math.floor((new Date(data.expires_at) - new Date()) / 1000);
          setCountdownSeconds(diff > 0 ? diff : 0);
        }
        setMessage('Codice OTP inviato alla tua email. Controlla anche la cartella spam.');
      } else {
        setMessage(data.error || data.message || 'Errore nell\'invio del codice');
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setMessage('Errore di connessione. Riprova.');
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const handleVerifyCode = async () => {
    const code = otpCode.trim();
    const email = formData.email.trim();
    
    if (code.length !== 6) {
      setMessage('Il codice deve essere di 6 cifre');
      return;
    }
    
    if (!verificationToken) {
      setMessage('Richiedi prima un codice OTP');
      return;
    }
    
    setIsVerifyingCode(true);
    setMessage('');
    
    try {
      const response = await fetch('/api/email_verification.php?action=verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          verification_token: verificationToken,
          code,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setEmailVerified(true);
        setMessage('Email verificata con successo!');
      } else {
        setMessage(data.error || data.message || 'Codice non valido');
      }
    } catch (error) {
      console.error('Code verification error:', error);
      setMessage('Errore di connessione. Riprova.');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleResendCode = async () => {
    const email = formData.email.trim();
    
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage('Inserisci un\'email valida');
      return;
    }
    
    if (!verificationToken) {
      handleVerifyEmail();
      return;
    }
    
    setIsVerifyingEmail(true);
    setMessage('');
    
    try {
      const response = await fetch('/api/email_verification.php?action=resend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          verification_token: verificationToken,
        }),
      });
      
      const data = await response.json();
      
      if (data.success && data.verification_token) {
        setVerificationToken(data.verification_token);
        if (data.expires_at) {
          setOtpExpiresAt(data.expires_at);
          const diff = Math.floor((new Date(data.expires_at) - new Date()) / 1000);
          setCountdownSeconds(diff > 0 ? diff : 0);
        }
        setMessage('Nuovo codice OTP inviato');
      } else {
        setMessage(data.error || data.message || 'Errore nell\'invio del codice');
      }
    } catch (error) {
      console.error('Resend code error:', error);
      setMessage('Errore di connessione. Riprova.');
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email verified
    if (!emailVerified || !verificationToken) {
      setMessage('Devi verificare l\'email prima di registrarti');
      return;
    }
    
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/standalone_register.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          verification_token: verificationToken,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Redirect to security questions setup
        navigate('/security-questions-setup', {
          state: {
            username: formData.username,
            email: formData.email,
            name: formData.name,
          },
        });
      } else {
        setMessage(data.message || 'Errore nella registrazione');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setMessage(t('register.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen p-4 bg-gray-100 dark:bg-gray-900">
      <div className="w-full max-w-md p-6 bg-white dark:bg-gray-800 rounded-xl shadow-md">
        <div className="flex justify-end mb-4">
          <LanguageSelector />
        </div>
        <h2 className="text-2xl font-bold mb-4 text-center text-gray-800 dark:text-white">
          {t('register.title')}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            name="username"
            placeholder={t('login.username')}
            className="w-full p-3 border rounded-lg focus:outline-none focus:ring"
            value={formData.username}
            onChange={handleChange}
            required
          />
          <input
            type="password"
            name="password"
            placeholder={t('login.password')}
            className="w-full p-3 border rounded-lg focus:outline-none focus:ring"
            value={formData.password}
            onChange={handleChange}
            required
          />
          {/* Email Field with Verification */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="email"
                name="email"
                placeholder={t('register.email')}
                className={`flex-1 p-3 border rounded-lg focus:outline-none focus:ring ${
                  emailVerified ? 'bg-gray-100 dark:bg-gray-700' : ''
                }`}
                value={formData.email}
                onChange={handleChange}
                disabled={emailVerified}
                required
              />
              {!emailVerified ? (
                <button
                  type="button"
                  onClick={handleVerifyEmail}
                  disabled={isVerifyingEmail || !formData.email}
                  className="px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isVerifyingEmail ? '...' : 'Verifica'}
                </button>
              ) : (
                <div className="flex items-center px-4">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
              )}
            </div>
            {emailVerified && (
              <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Email verificata
              </p>
            )}
          </div>

          {/* OTP Field (show only if token exists and not verified) */}
          {verificationToken && !emailVerified && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Codice OTP (6 cifre)"
                  maxLength={6}
                  className="flex-1 p-3 border rounded-lg focus:outline-none focus:ring"
                  value={otpCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setOtpCode(value);
                  }}
                />
                <button
                  type="button"
                  onClick={handleVerifyCode}
                  disabled={isVerifyingCode || otpCode.length !== 6}
                  className="px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isVerifyingCode ? '...' : 'Conferma'}
                </button>
              </div>
              {countdownSeconds > 0 ? (
                <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Puoi richiedere un nuovo codice tra {countdownSeconds}s
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isVerifyingEmail}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                >
                  <RefreshCw className={`h-3 w-3 ${isVerifyingEmail ? 'animate-spin' : ''}`} />
                  Invia nuovo codice
                </button>
              )}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-2">
                <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-800 dark:text-blue-300">
                  💡 Non vedi l'email? Controlla anche la cartella <strong>spam</strong> o <strong>posta indesiderata</strong>.
                </p>
              </div>
            </div>
          )}
          <input
            type="text"
            name="name"
            placeholder={t('register.fullName')}
            className="w-full p-3 border rounded-lg focus:outline-none focus:ring"
            value={formData.name}
            onChange={handleChange}
            required
          />
          <button
            type="submit"
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !emailVerified}
          >
            {loading ? t('register.registering') : t('register.registerButton')}
          </button>
          {!emailVerified && (
            <p className="text-xs text-center text-gray-600 dark:text-gray-400">
              Verifica l'email per abilitare la registrazione
            </p>
          )}
          <div className="text-center mt-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('register.alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-semibold transition">
                {t('register.backToLogin')}
              </Link>
            </p>
          </div>
        </form>
        {message && <p className="text-center text-sm mt-4 text-gray-700 dark:text-gray-300">{message}</p>}
        
        {/* Privacy and Cookie Policy Links */}
        <div className="mt-8 mb-0 text-center">
          <div className="flex flex-col items-center space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center justify-center mb-0 pb-0">
              <a 
                href="https://www.iubenda.com/privacy-policy/65746024" 
                className="iubenda-black iubenda-noiframe hover:text-indigo-600 dark:hover:text-indigo-400 transition"
                title="Privacy Policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              <span className="mx-4 text-gray-400 dark:text-gray-600">•</span>
              <a 
                href="https://www.iubenda.com/privacy-policy/65746024/cookie-policy" 
                className="iubenda-black iubenda-noiframe hover:text-indigo-600 dark:hover:text-indigo-400 transition"
                title="Cookie Policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}